/**
 * Rendezvous / signaling client — the browser end of the hosted relay
 * (@fume/app-rendezvous). It lets the two devices exchange WebRTC
 * offer/answer/ICE over a WebSocket so we can retire the copy/paste handshake.
 *
 * HONEST SCOPE (load-bearing): this client relays ONLY opaque signaling blobs —
 * the SDP offer/answer. The session SEED never travels here; it rides the
 * share-link URL fragment (`#…`), delivered peer-to-peer over whatever OOB
 * channel the humans already use (Signal, AirDrop, in person / QR), exactly like
 * the old invite code. So the relay — like the old copy/paste channel — never
 * sees key material or plaintext. It only ever handles SDP/ICE, which every
 * signaling point must.
 *
 * The endpoint is `VITE_RENDEZVOUS_URL` (a `wss://` URL). When unset the client
 * reports `configured === false` and the app stays on copy/paste; hosted connect
 * is offered only when a relay is configured.
 */

/** A live signaling channel to exactly one peer, brokered by the relay. */
export interface RendezvousSession {
  /** The room id. Present immediately for a guest; set after `created` for a host. */
  readonly rendezvousId: string;
  /** Relay one opaque signaling blob (offer/answer SDP) to the peer. */
  send(message: unknown): void;
  /** Inbound opaque signaling blobs from the peer. */
  onMessage(handler: (message: unknown) => void): void;
  /** Host-only: the guest has joined the room (safe to push the offer). */
  onPeerJoined(handler: () => void): void;
  /** The relay side ended: 'peer-left' | 'expired' | 'socket-closed' | 'error'. */
  onClosed(handler: (reason: string) => void): void;
  /** Close the signaling channel. The live P2P data channel is unaffected. */
  close(): void;
}

export interface RendezvousClientOptions {
  /** URL of the hosted relay. Defaults to `import.meta.env.VITE_RENDEZVOUS_URL`. */
  readonly endpoint?: string;
  /** Handshake timeout in ms before create/join rejects (default 15_000). */
  readonly connectTimeoutMs?: number;
}

const env = import.meta.env as Record<string, string | undefined>;

type ControlHandler = (msg: any) => void;

class WsRendezvousSession implements RendezvousSession {
  rendezvousId: string;
  private readonly ws: WebSocket;
  private msgHandler: ((message: unknown) => void) | null = null;
  private peerJoinedHandler: (() => void) | null = null;
  private closedHandler: ((reason: string) => void) | null = null;
  private closedFired = false;
  // Buffer events that arrive before the app attaches its handlers (the offer
  // can land between join-ack and onMessage being set — a real guest-side race).
  private readonly pendingMsgs: unknown[] = [];
  private peerJoinedPending = false;

  constructor(ws: WebSocket, rendezvousId: string, onControl: ControlHandler) {
    this.ws = ws;
    this.rendezvousId = rendezvousId;
    ws.addEventListener('message', (e: MessageEvent) => {
      let msg: any;
      try {
        msg = JSON.parse(typeof e.data === 'string' ? e.data : '');
      } catch {
        return;
      }
      onControl(msg);
      switch (msg?.t) {
        case 'signal':
          if (this.msgHandler) this.msgHandler(msg.data);
          else this.pendingMsgs.push(msg.data);
          break;
        case 'peer-joined':
          if (this.peerJoinedHandler) this.peerJoinedHandler();
          else this.peerJoinedPending = true;
          break;
        case 'peer-left':
        case 'expired':
          this.fireClosed(msg.t);
          break;
        default:
          break;
      }
    });
    ws.addEventListener('close', () => this.fireClosed('socket-closed'));
    ws.addEventListener('error', () => this.fireClosed('error'));
  }

  private fireClosed(reason: string): void {
    if (this.closedFired) return;
    this.closedFired = true;
    this.closedHandler?.(reason);
  }

  send(message: unknown): void {
    if (this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ t: 'signal', data: message }));
    }
  }
  onMessage(handler: (message: unknown) => void): void {
    this.msgHandler = handler;
    if (this.pendingMsgs.length > 0) {
      const queued = this.pendingMsgs.splice(0);
      for (const m of queued) handler(m);
    }
  }
  onPeerJoined(handler: () => void): void {
    this.peerJoinedHandler = handler;
    if (this.peerJoinedPending) {
      this.peerJoinedPending = false;
      handler();
    }
  }
  onClosed(handler: (reason: string) => void): void {
    this.closedHandler = handler;
  }
  close(): void {
    try {
      if (this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ t: 'bye' }));
      this.ws.close();
    } catch {
      /* already gone */
    }
  }
}

export class RendezvousClient {
  private readonly endpoint: string | undefined;
  private readonly connectTimeoutMs: number;

  constructor(options: RendezvousClientOptions = {}) {
    this.endpoint = options.endpoint ?? env.VITE_RENDEZVOUS_URL;
    this.connectTimeoutMs = options.connectTimeoutMs ?? 15_000;
  }

  /** True when a relay URL is configured; the app gates hosted connect on this. */
  get configured(): boolean {
    return typeof this.endpoint === 'string' && this.endpoint.length > 0;
  }

  /** Host: open a room and get its id. Resolves once the relay confirms `created`. */
  async createSession(ttlSeconds?: number): Promise<RendezvousSession> {
    return this.open({ t: 'create', ttlSeconds }, 'created');
  }

  /** Guest: join a room by id. Resolves once the relay confirms `joined`. */
  async joinSession(rendezvousId: string): Promise<RendezvousSession> {
    return this.open({ t: 'join', id: rendezvousId }, 'joined', rendezvousId);
  }

  private open(hello: unknown, ackType: string, knownId = ''): Promise<RendezvousSession> {
    if (!this.configured) {
      return Promise.reject(new Error('No rendezvous relay configured (VITE_RENDEZVOUS_URL is unset).'));
    }
    const url = this.endpoint as string;
    return new Promise<RendezvousSession>((resolve, reject) => {
      let settled = false;
      let session: WsRendezvousSession | null = null;
      const ws = new WebSocket(url);

      const timer = setTimeout(() => {
        if (settled) return;
        settled = true;
        try {
          ws.close();
        } catch {
          /* noop */
        }
        reject(new Error('Rendezvous relay did not respond in time.'));
      }, this.connectTimeoutMs);

      const onControl: ControlHandler = (msg) => {
        if (settled) return;
        if (msg?.t === ackType) {
          settled = true;
          clearTimeout(timer);
          session!.rendezvousId = typeof msg.id === 'string' ? msg.id : knownId;
          resolve(session!);
        } else if (msg?.t === 'throttled') {
          settled = true;
          clearTimeout(timer);
          try {
            ws.close();
          } catch {
            /* noop */
          }
          reject(new Error('The relay is rate-limiting connections; try again in a moment.'));
        }
      };

      // Build the session wrapper now so it captures every inbound frame,
      // including the `created`/`joined` ack routed through onControl.
      session = new WsRendezvousSession(ws, knownId, onControl);

      ws.addEventListener('open', () => ws.send(JSON.stringify(hello)));
      ws.addEventListener('error', () => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        reject(new Error('Could not reach the rendezvous relay.'));
      });
    });
  }
}

/** Convenience: is a relay configured in this build? */
export function isRendezvousConfigured(): boolean {
  return typeof env.VITE_RENDEZVOUS_URL === 'string' && env.VITE_RENDEZVOUS_URL.length > 0;
}
